'use client'

import { createContext, useContext, useMemo } from 'react'

import type { StaticTrackingSecondaryLocation } from '../_hooks/useStaticTracking'

export type TrackingLocation = {
  // Subsection of the page defined in code, e.g. a PDP section.
  secondaryTrackingLocation?: StaticTrackingSecondaryLocation

  moduleName?: string | null
  // 1-based order of the page section (module) the element is in.
  sectionNumber?: number
  // 1-based position of the element within its list, when it is one of many.
  position?: number
  // Inside a Quickbuy dialog. `secondaryTrackingLocation` still says where it was opened from.
  isQuickbuy?: boolean
}

const TrackingLocationContext = createContext<TrackingLocation>({})

/**
 * Describes where in the page its children are, for analytics. Nested providers override only the
 * values they set, so a page section can set `sectionNumber` and each tile its `position`.
 */
export const TrackingLocationProvider = ({
  secondaryTrackingLocation,
  moduleName,
  sectionNumber,
  position,
  isQuickbuy,
  children,
}: TrackingLocation & { children?: React.ReactNode }) => {
  const parent = useContext(TrackingLocationContext)
  const value = useMemo(
    () => ({
      secondaryTrackingLocation: secondaryTrackingLocation ?? parent.secondaryTrackingLocation,
      moduleName: moduleName ?? parent.moduleName,
      sectionNumber: sectionNumber ?? parent.sectionNumber,
      position: position ?? parent.position,
      isQuickbuy: isQuickbuy ?? parent.isQuickbuy,
    }),
    [parent, secondaryTrackingLocation, moduleName, sectionNumber, position, isQuickbuy]
  )
  return (
    <TrackingLocationContext.Provider value={value}>{children}</TrackingLocationContext.Provider>
  )
}

export const useTrackingLocation = () => useContext(TrackingLocationContext)

// Code-defined sections win over the Contentful module they're nested in.
export const resolveSecondaryLocation = (trackingLocation: TrackingLocation): string | null =>
  trackingLocation.secondaryTrackingLocation ?? trackingLocation.moduleName ?? null
